import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { campaignAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    ArrowPathIcon,
    FunnelIcon,
    TrashIcon,
    CheckIcon,
    XMarkIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const CampaignDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [campaign, setCampaign] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [pagination, setPagination] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [overallStats, setOverallStats] = useState({
        totalContacts: 0,
        sent: 0,
        delivered: 0,
        failed: 0,
        pending: 0
    });

    // Bulk delete states
    const [selectedContacts, setSelectedContacts] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteMode, setDeleteMode] = useState('selected'); // 'selected' or 'status'
    const [statusToDelete, setStatusToDelete] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Bulk status update states
    const [fromStatus, setFromStatus] = useState('');
    const [toStatus, setToStatus] = useState('');
    const [updateCount, setUpdateCount] = useState('');
    const [updating, setUpdating] = useState(false);

    // Bulk pulse update states
    const [pulseEntries, setPulseEntries] = useState([{ count: '', pulse: '' }]);
    const [updatingPulse, setUpdatingPulse] = useState(false);

    useEffect(() => {
        fetchCampaignContacts();
    }, [id, searchTerm, statusFilter, currentPage]);

    // Refresh data when returning from another tab (e.g., after editing)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchCampaignContacts();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [id]);

    const fetchCampaignContacts = async () => {
        try {
            setLoading(true);
            const response = await campaignAPI.getCampaignContacts(id, {
                page: currentPage,
                limit: 10,
                search: searchTerm,
                status: statusFilter
            });

            const data = response.data.data;
            setContacts(data.contacts);
            setCampaign(data.campaign);
            setPagination(data.pagination);
            setOverallStats(data.campaign.overallStats || {
                totalContacts: 0,
                sent: 0,
                delivered: 0,
                failed: 0,
                pending: 0
            });

            // Reset bulk selection when data changes
            setSelectedContacts(new Set());
            setSelectAll(false);
            setShowBulkActions(false);

        } catch (error) {
            toast.error('Failed to load campaign details');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchCampaignContacts();
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            pending: 'bg-yellow-100 text-yellow-800',
            sent: 'bg-blue-100 text-blue-800',
            delivered: 'bg-green-100 text-green-800',
            failed: 'bg-red-100 text-red-800'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    // Bulk delete functions
    const handleContactSelect = (contactId) => {
        const newSelected = new Set(selectedContacts);
        if (newSelected.has(contactId)) {
            newSelected.delete(contactId);
        } else {
            newSelected.add(contactId);
        }
        setSelectedContacts(newSelected);
        setShowBulkActions(newSelected.size > 0);
        setSelectAll(newSelected.size === contacts.length);
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedContacts(new Set());
            setShowBulkActions(false);
        } else {
            const allContactIds = new Set(contacts.map(contact => contact._id));
            setSelectedContacts(allContactIds);
            setShowBulkActions(true);
        }
        setSelectAll(!selectAll);
    };

    const handleBulkDelete = (mode, status = '') => {
        setDeleteMode(mode);
        setStatusToDelete(status);
        setShowDeleteModal(true);
        setDeleteConfirmText('');
    };

    const confirmBulkDelete = async () => {
        if (deleteMode === 'selected' && deleteConfirmText !== 'DELETE') {
            toast.error('Please type "DELETE" to confirm');
            return;
        }
        if (deleteMode === 'status' && deleteConfirmText !== `DELETE ${statusToDelete.toUpperCase()}`) {
            toast.error(`Please type "DELETE ${statusToDelete.toUpperCase()}" to confirm`);
            return;
        }

        try {
            setDeleting(true);

            const deleteData = deleteMode === 'selected'
                ? { contactIds: Array.from(selectedContacts) }
                : { deleteByStatus: true, statusToDelete: statusToDelete };

            const response = await campaignAPI.bulkDeleteContacts(id, deleteData);

            toast.success(response.data.message);

            // Refresh the data to show updated statistics
            await fetchCampaignContacts();

            // Close modal and reset states
            setShowDeleteModal(false);
            setSelectedContacts(new Set());
            setSelectAll(false);
            setShowBulkActions(false);
            setDeleteConfirmText('');

        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete contacts');
            console.error('Bulk delete error:', error);
        } finally {
            setDeleting(false);
        }
    };

    const getDeleteConfirmationText = () => {
        if (deleteMode === 'selected') {
            return `Are you sure you want to delete ${selectedContacts.size} selected contacts? This action cannot be undone.`;
        } else {
            const statusCount = overallStats[statusToDelete.toLowerCase()] || 0;
            return `Are you sure you want to delete all ${statusCount} contacts with "${statusToDelete}" status? This action cannot be undone.`;
        }
    };

    const getRequiredConfirmText = () => {
        return deleteMode === 'selected' ? 'DELETE' : `DELETE ${statusToDelete.toUpperCase()}`;
    };

    // Bulk status update function
    const handleBulkStatusUpdate = async () => {
        if (!fromStatus || !toStatus || !updateCount || updateCount <= 0) {
            toast.error('Please fill all fields with valid values');
            return;
        }

        if (fromStatus === toStatus) {
            toast.error('From status and to status cannot be the same');
            return;
        }

        const availableCount = overallStats[fromStatus.toLowerCase()] || 0;
        if (parseInt(updateCount) > availableCount) {
            toast.error(`Only ${availableCount} contacts available with ${fromStatus} status`);
            return;
        }

        try {
            setUpdating(true);
            const response = await campaignAPI.bulkStatusUpdate(id, {
                fromStatus,
                toStatus,
                count: parseInt(updateCount)
            });

            toast.success(response.data.message);

            // Refresh the data to show updated statistics
            await fetchCampaignContacts();

            // Reset form
            setFromStatus('');
            setToStatus('');
            setUpdateCount('');

        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update contact statuses');
            console.error('Bulk status update error:', error);
        } finally {
            setUpdating(false);
        }
    };

    const getAvailableCount = (status) => {
        return overallStats[status.toLowerCase()] || 0;
    };

    const handlePulseEntryChange = (index, field, value) => {
        const updated = [...pulseEntries];
        updated[index] = { ...updated[index], [field]: value };
        setPulseEntries(updated);
    };

    const addPulseEntry = () => {
        setPulseEntries([...pulseEntries, { count: '', pulse: '' }]);
    };

    const removePulseEntry = (index) => {
        if (pulseEntries.length <= 1) return;
        setPulseEntries(pulseEntries.filter((_, i) => i !== index));
    };

    const getTotalPulseCount = () => {
        return pulseEntries.reduce((sum, e) => sum + (parseInt(e.count) || 0), 0);
    };

    const isPulseFormValid = () => {
        if (pulseEntries.length === 0 || overallStats.delivered === 0) return false;
        const hasEmpty = pulseEntries.some(e => !e.count || parseInt(e.count) <= 0 || !e.pulse || parseFloat(e.pulse) <= 0);
        if (hasEmpty) return false;
        return getTotalPulseCount() <= overallStats.delivered;
    };

    const handleBulkPulseUpdate = async () => {
        if (!isPulseFormValid()) {
            toast.error('Please fill all rows with valid values');
            return;
        }

        const totalCount = getTotalPulseCount();
        if (totalCount > overallStats.delivered) {
            toast.error(`Total count (${totalCount.toLocaleString()}) exceeds delivered contacts (${overallStats.delivered.toLocaleString()})`);
            return;
        }

        const entries = pulseEntries.map(e => ({
            count: parseInt(e.count),
            pulse: parseFloat(e.pulse)
        }));

        try {
            setUpdatingPulse(true);
            const response = await campaignAPI.bulkPulseUpdate(id, { entries });
            toast.success(response.data.message);
            await fetchCampaignContacts();
            setPulseEntries([{ count: '', pulse: '' }]);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update pulse');
        } finally {
            setUpdatingPulse(false);
        }
    };

    if (loading && !campaign) {
        return (
            <div className="space-y-6">
                {/* Header skeleton */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 bg-soft-200 rounded-lg animate-pulse"></div>
                        <div>
                            <div className="h-8 bg-soft-200 rounded-lg w-64 animate-pulse"></div>
                            <div className="h-4 bg-soft-100 rounded-lg w-48 mt-2 animate-pulse"></div>
                        </div>
                    </div>
                    <div className="h-10 bg-soft-200 rounded-lg w-24 animate-pulse"></div>
                </div>

                {/* Stats cards skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, index) => (
                        <div key={index} className="card">
                            <div className="space-y-3">
                                <div className="h-4 bg-soft-200 rounded w-24 animate-pulse"></div>
                                <div className="h-8 bg-soft-100 rounded w-16 animate-pulse"></div>
                                <div className="h-3 bg-soft-200 rounded w-20 animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Campaign details skeleton */}
                <div className="card">
                    <div className="space-y-6">
                        {/* Loading header with animation */}
                        <div className="text-center py-8">
                            <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                                <div className="absolute inset-0 rounded-full border-4 border-primary-200 animate-spin border-t-primary-600"></div>
                                <div className="absolute inset-2 rounded-full border-2 border-primary-100 animate-spin border-t-primary-400" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                                <MagnifyingGlassIcon className="h-6 w-6 text-primary-600 animate-pulse" />
                            </div>
                            <h3 className="text-lg font-semibold text-soft-900 mb-2">Loading Campaign Details</h3>
                            <p className="text-soft-600 mb-6">Analyzing campaign performance and contact data...</p>

                            {/* Progress dots */}
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                        </div>

                        {/* Search and filters skeleton */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1 h-10 bg-soft-100 rounded-lg animate-pulse"></div>
                            <div className="w-32 h-10 bg-soft-100 rounded-lg animate-pulse"></div>
                        </div>

                        {/* Table skeleton */}
                        <div className="overflow-hidden">
                            {/* Table header skeleton */}
                            <div className="bg-soft-50 px-6 py-3 border-b border-soft-200">
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                </div>
                            </div>

                            {/* Contact row skeletons */}
                            {[...Array(8)].map((_, index) => (
                                <div key={index} className="px-6 py-4 border-b border-soft-100">
                                    <div className="grid grid-cols-4 gap-4 items-center">
                                        <div className="h-4 bg-soft-200 rounded w-32 animate-pulse"></div>
                                        <div className="h-6 w-20 bg-soft-200 rounded-full animate-pulse"></div>
                                        <div className="h-4 bg-soft-200 rounded w-24 animate-pulse"></div>
                                        <div className="h-4 bg-soft-200 rounded w-28 animate-pulse"></div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination skeleton */}
                        <div className="flex items-center justify-between pt-4">
                            <div className="h-4 bg-soft-200 rounded w-40 animate-pulse"></div>
                            <div className="flex space-x-2">
                                <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                                <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                                <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading message */}
                <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-soft-500 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-soft-300 border-t-primary-500"></div>
                        <span className="animate-pulse">Processing contact engagement data...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className="text-center py-12">
                <p className="text-soft-500">Campaign not found</p>
            </div>
        );
    }

    return (
        <div className="page-container">
            {/* Back Button - Positioned at the top */}
            <div className="mb-2">
                <button
                    onClick={() => navigate('/campaigns')}
                    className="group inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-transparent hover:bg-gray-50 rounded-lg transition-all duration-200"
                >
                    <ArrowLeftIcon className="h-4 w-4 group-hover:-translate-x-1 transition-transform duration-200" />
                    <span>Back to Campaigns</span>
                </button>
            </div>

            {/* Header */}
            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-soft-900">Campaign Details</h1>
                        <div className="flex items-center space-x-3 mt-2">
                            <span className="text-lg font-medium text-soft-700">{campaign.title}</span>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                                {campaign.type}
                            </span>
                            <span className="text-sm text-soft-500">
                                Created {new Date(campaign.createdAt || Date.now()).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                    <div>
                        <button
                            onClick={fetchCampaignContacts}
                            disabled={loading}
                            className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-all duration-200"
                            title="Refresh campaign data"
                        >
                            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Campaign Overview Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-blue-700">
                                {(campaign.statistics?.totalNumbers || overallStats.totalContacts).toLocaleString()}
                            </p>
                            <p className="text-sm text-blue-600 font-medium">Total Contacts</p>
                        </div>
                        <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-green-700">{overallStats.delivered.toLocaleString()}</p>
                            <p className="text-sm text-green-600 font-medium">Delivered</p>
                            <p className="text-xs text-green-500 mt-1">
                                {(() => {
                                    const totalForCalc = campaign.statistics?.totalNumbers || overallStats.totalContacts;
                                    return totalForCalc > 0 ? `${((overallStats.delivered / totalForCalc) * 100).toFixed(1)}%` : '0%';
                                })()}
                            </p>
                        </div>
                        <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center">
                            <CheckIcon className="w-5 h-5 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-xl border border-yellow-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-yellow-700">{overallStats.sent.toLocaleString()}</p>
                            <p className="text-sm text-yellow-600 font-medium">Sent</p>
                            <p className="text-xs text-yellow-500 mt-1">
                                {(() => {
                                    const totalForCalc = campaign.statistics?.totalNumbers || overallStats.totalContacts;
                                    return totalForCalc > 0 ? `${((overallStats.sent / totalForCalc) * 100).toFixed(1)}%` : '0%';
                                })()}
                            </p>
                        </div>
                        <div className="w-10 h-10 bg-yellow-200 rounded-lg flex items-center justify-center">
                            <ArrowPathIcon className="w-5 h-5 text-yellow-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-gray-700">
                                {(overallStats.failed + overallStats.pending).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-600 font-medium">Remaining</p>
                            <p className="text-xs text-gray-500 mt-1">
                                Failed: {overallStats.failed} | Pending: {overallStats.pending}
                            </p>
                        </div>
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <ExclamationTriangleIcon className="w-5 h-5 text-gray-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Layout */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Left Column - Admin Tools */}
                {isAdmin() && (
                    <div className="xl:col-span-1 space-y-4">
                        {/* Admin Status Update Tool */}
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <ArrowPathIcon className="w-4 h-4 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Bulk Status Update</h3>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">From Status</label>
                                    <select
                                        value={fromStatus}
                                        onChange={(e) => setFromStatus(e.target.value)}
                                        disabled={updating}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                                    >
                                        <option value="">Select status</option>
                                        {['pending', 'sent', 'delivered', 'failed'].map(status => {
                                            const count = getAvailableCount(status);
                                            return (
                                                <option key={status} value={status} disabled={count === 0}>
                                                    {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">To Status</label>
                                    <select
                                        value={toStatus}
                                        onChange={(e) => setToStatus(e.target.value)}
                                        disabled={updating}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                                    >
                                        <option value="">Select status</option>
                                        {['pending', 'sent', 'delivered', 'failed'].map(status => (
                                            <option key={status} value={status}>
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Count
                                        {fromStatus && (
                                            <span className="text-blue-600 font-normal"> (Max: {getAvailableCount(fromStatus)})</span>
                                        )}
                                    </label>
                                    <input
                                        type="number"
                                        value={updateCount}
                                        onChange={(e) => setUpdateCount(e.target.value)}
                                        disabled={updating}
                                        min="1"
                                        max={fromStatus ? getAvailableCount(fromStatus) : undefined}
                                        placeholder="Enter count"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                                    />
                                </div>

                                {fromStatus && toStatus && updateCount && fromStatus !== toStatus && (
                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-sm text-blue-800">
                                            <strong>Preview:</strong> Will update {updateCount} contact{parseInt(updateCount) !== 1 ? 's' : ''} from
                                            <span className="font-semibold"> {fromStatus}</span> to
                                            <span className="font-semibold"> {toStatus}</span> status.
                                            {toStatus === 'delivered' && <span className="text-green-600"> (Pulse will be set to 1)</span>}
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={handleBulkStatusUpdate}
                                    disabled={updating || !fromStatus || !toStatus || !updateCount || fromStatus === toStatus}
                                    className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {updating ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <ArrowPathIcon className="w-4 h-4 mr-2" />
                                            Update Status
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Quick Delete Actions */}
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                                    <TrashIcon className="w-4 h-4 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Quick Delete</h3>
                            </div>

                            <div className="space-y-2">
                                {['pending', 'sent', 'delivered', 'failed'].map(status => {
                                    const count = overallStats[status] || 0;
                                    return count > 0 ? (
                                        <button
                                            key={status}
                                            onClick={() => handleBulkDelete('status', status)}
                                            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border transition-colors ${status === 'pending' ? 'text-yellow-700 bg-yellow-50 border-yellow-200 hover:bg-yellow-100' :
                                                status === 'sent' ? 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100' :
                                                    status === 'delivered' ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100' :
                                                        'text-red-700 bg-red-50 border-red-200 hover:bg-red-100'
                                                }`}
                                        >
                                            <span>Delete all {status}</span>
                                            <span className="font-semibold">{count}</span>
                                        </button>
                                    ) : null;
                                })}

                                {showBulkActions && (
                                    <button
                                        onClick={() => handleBulkDelete('selected')}
                                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                                    >
                                        <span>Delete selected</span>
                                        <span className="font-semibold">{selectedContacts.size}</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Bulk Pulse Update - Voice Call campaigns only */}
                        {campaign.type === 'Voice Call' && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Bulk Pulse Update</h3>
                                </div>

                                <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-purple-700">Delivered Contacts</span>
                                        <span className="text-lg font-bold text-purple-800">{overallStats.delivered.toLocaleString()}</span>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Records & Pulse</span>
                                        <button
                                            type="button"
                                            onClick={addPulseEntry}
                                            disabled={updatingPulse}
                                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 disabled:opacity-50 transition-colors"
                                        >
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            Add Row
                                        </button>
                                    </div>

                                    {pulseEntries.map((entry, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <div className="flex-1">
                                                <input
                                                    type="number"
                                                    value={entry.count}
                                                    onChange={(e) => handlePulseEntryChange(index, 'count', e.target.value)}
                                                    disabled={updatingPulse}
                                                    min="1"
                                                    placeholder="Records"
                                                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    type="number"
                                                    value={entry.pulse}
                                                    onChange={(e) => handlePulseEntryChange(index, 'pulse', e.target.value)}
                                                    disabled={updatingPulse}
                                                    min="1"
                                                    step="1"
                                                    placeholder="Pulse (sec)"
                                                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removePulseEntry(index)}
                                                disabled={updatingPulse || pulseEntries.length <= 1}
                                                className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                                                title="Remove row"
                                            >
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}

                                    {/* Total & Validation */}
                                    <div className={`p-3 rounded-lg border ${getTotalPulseCount() > overallStats.delivered ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'}`}>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className={getTotalPulseCount() > overallStats.delivered ? 'text-red-700' : 'text-purple-700'}>
                                                Total Records
                                            </span>
                                            <span className={`font-bold ${getTotalPulseCount() > overallStats.delivered ? 'text-red-800' : 'text-purple-800'}`}>
                                                {getTotalPulseCount().toLocaleString()} / {overallStats.delivered.toLocaleString()}
                                            </span>
                                        </div>
                                        {getTotalPulseCount() > overallStats.delivered && (
                                            <p className="text-xs text-red-600 mt-1">
                                                Total exceeds delivered contacts by {(getTotalPulseCount() - overallStats.delivered).toLocaleString()}
                                            </p>
                                        )}
                                        {getTotalPulseCount() > 0 && getTotalPulseCount() <= overallStats.delivered && (
                                            <div className="mt-2 space-y-1">
                                                {pulseEntries.filter(e => parseInt(e.count) > 0 && parseFloat(e.pulse) > 0).map((e, i) => (
                                                    <p key={i} className="text-xs text-purple-600">
                                                        {parseInt(e.count).toLocaleString()} contacts → {e.pulse}s pulse
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={handleBulkPulseUpdate}
                                        disabled={updatingPulse || !isPulseFormValid()}
                                        className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {updatingPulse ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                Updating Pulse...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                </svg>
                                                Update All Pulse
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Right Column - Contacts Table */}
                <div className={`${isAdmin() ? 'xl:col-span-2' : 'xl:col-span-3'}`}>
                    <div className="bg-white rounded-lg border border-gray-200">
                        {/* Table Header with Search and Filters */}
                        <div className="p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                <div className="flex-1">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search phone numbers..."
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <FunnelIcon className="h-5 w-5 text-gray-400" />
                                        <select
                                            value={statusFilter}
                                            onChange={handleStatusFilterChange}
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">All Status</option>
                                            <option value="pending">Pending ({overallStats.pending})</option>
                                            <option value="sent">Sent ({overallStats.sent})</option>
                                            <option value="delivered">Delivered ({overallStats.delivered})</option>
                                            <option value="failed">Failed ({overallStats.failed})</option>
                                        </select>
                                    </div>

                                    <button
                                        onClick={fetchCampaignContacts}
                                        disabled={loading}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-all duration-200"
                                        title="Refresh contacts"
                                    >
                                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Contacts Table */}
                        <div className="overflow-x-auto">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-soft-200">
                                    <thead className="bg-soft-50">
                                        <tr>
                                            {isAdmin() && (
                                                <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                                    <div className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectAll}
                                                            onChange={handleSelectAll}
                                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                        />
                                                        <span className="ml-2">Select</span>
                                                    </div>
                                                </th>
                                            )}
                                            <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                                Mobile Number
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                                Timestamp
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                                Pulse (Seconds)
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-soft-200">
                                        {loading ? (
                                            <tr>
                                                <td colSpan={isAdmin() ? "5" : "4"} className="px-6 py-4 text-center">
                                                    <div className="flex items-center justify-center">
                                                        <div className="loading-spinner"></div>
                                                        <span className="ml-2 text-soft-600">Loading contacts...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : contacts.length > 0 ? (
                                            contacts.map((contact, index) => (
                                                <tr key={contact._id || index} className="hover:bg-soft-50">
                                                    {isAdmin() && (
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedContacts.has(contact._id)}
                                                                onChange={() => handleContactSelect(contact._id)}
                                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                            />
                                                        </td>
                                                    )}
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-soft-900">
                                                        {contact.mobile}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-soft-600">
                                                        {formatDate(contact.timestamp)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {getStatusBadge(contact.status)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-soft-600">
                                                        {contact.pulse !== null ? contact.pulse : 'N/A'}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={isAdmin() ? "5" : "4"} className="px-6 py-4 text-center text-soft-500">
                                                    {searchTerm ? 'No contacts found matching your search.' : 'No contacts found.'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.pages > 1 && (
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-soft-200">
                                    <div className="text-xs sm:text-sm text-soft-700 text-center sm:text-left">
                                        Showing page {pagination.current} of {pagination.pages} (Total: {pagination.total})
                                    </div>
                                    <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
                                        <button
                                            onClick={() => handlePageChange(pagination.current - 1)}
                                            disabled={pagination.current === 1}
                                            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                                        >
                                            <span className="hidden sm:inline">Previous</span>
                                            <span className="sm:hidden">Prev</span>
                                        </button>

                                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                            const page = i + Math.max(1, pagination.current - 2);
                                            if (page > pagination.pages) return null;

                                            return (
                                                <button
                                                    key={page}
                                                    onClick={() => handlePageChange(page)}
                                                    className={`px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm rounded ${page === pagination.current
                                                        ? 'bg-primary-600 text-white'
                                                        : 'bg-white text-soft-700 border border-soft-300 hover:bg-soft-50'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => handlePageChange(pagination.current + 1)}
                                            disabled={pagination.current === pagination.pages}
                                            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                                        >
                                            <span className="hidden sm:inline">Next</span>
                                            <span className="sm:hidden">Next</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bulk Delete Confirmation Modal */}
                        {showDeleteModal && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        {/* Modal Header */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                                                </div>
                                                <div className="ml-4">
                                                    <h3 className="text-lg font-medium text-gray-900">
                                                        Confirm Bulk Delete
                                                    </h3>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowDeleteModal(false)}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        {/* Modal Body */}
                                        <div className="mb-4">
                                            <p className="text-sm text-gray-500 mb-4">
                                                {getDeleteConfirmationText()}
                                            </p>

                                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                                                <div className="flex">
                                                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                                                    <div className="ml-3">
                                                        <h3 className="text-sm font-medium text-yellow-800">
                                                            Warning
                                                        </h3>
                                                        <div className="mt-2 text-sm text-yellow-700">
                                                            <p>This will permanently delete the contacts and update campaign statistics. Credits will be refunded to the campaign creator.</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Type "{getRequiredConfirmText()}" to confirm:
                                                </label>
                                                <input
                                                    type="text"
                                                    value={deleteConfirmText}
                                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                    placeholder={getRequiredConfirmText()}
                                                />
                                            </div>
                                        </div>

                                        {/* Modal Footer */}
                                        <div className="flex flex-col sm:flex-row items-center justify-end gap-3 sm:gap-3">
                                            <button
                                                onClick={() => setShowDeleteModal(false)}
                                                disabled={deleting}
                                                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={confirmBulkDelete}
                                                disabled={deleting || deleteConfirmText !== getRequiredConfirmText()}
                                                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {deleting ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                        <span>Deleting...</span>
                                                    </div>
                                                ) : (
                                                    <span className="hidden sm:inline">Delete Contacts</span>
                                                )}
                                                {!deleting && <span className="sm:hidden">Delete</span>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignDetail; 